import type { LocalizedString } from '@web-studio/shared-schemas';
import {
  LEGAL_IMPRINT_DEFAULT_LABELS,
  LEGAL_IMPRINT_FIELD_KEYS,
  LEGAL_PRIVACY_DEFAULT_LABELS,
  LEGAL_PRIVACY_FIELD_KEYS,
  legalFieldLabelContentKey,
} from '@web-studio/shared-schemas';
import { pickLocalized } from './localized';

/** Site chrome defaults. Cookie settings cover the full platform locale catalog;
 * most other keys are de / en / vi until expanded. reservationNext* also seed
 * the editable `nextStep` content field on channel variants. */
export const SITE_CHROME = {
  footerContact: { de: 'Kontakt', en: 'Contact', vi: 'Liên hệ' },
  footerHours: { de: 'Öffnungszeiten', en: 'Opening hours', vi: 'Giờ mở cửa' },
  footerSocial: { de: 'Social Media', en: 'Social', vi: 'Mạng xã hội' },
  /** Edit-mode empty affordance — focuses Zone 4 Social-links field. */
  footerSocialEmpty: {
    de: 'Profile hinzufügen',
    en: 'Add profiles',
    vi: 'Thêm hồ sơ',
  },
  footerSocialEmptyHint: {
    de: 'Noch keine Links — tippen zum Bearbeiten im Seitenpanel.',
    en: 'No links yet — tap to edit in the side panel.',
    vi: 'Chưa có liên kết — chạm để chỉnh ở bảng bên.',
  },
  legalNav: { de: 'Rechtliches', en: 'Legal', vi: 'Pháp lý' },
  /** Footer + bar — reopen cookie notice (Germany TTDSG / GDPR). Platform chrome for all catalog locales. */
  cookieSettingsLabel: {
    de: 'Cookie-Einstellungen',
    en: 'Cookie settings',
    es: 'Configuración de cookies',
    fr: 'Paramètres des cookies',
    it: 'Impostazioni dei cookie',
    nl: 'Cookie-instellingen',
    pl: 'Ustawienia plików cookie',
    tr: 'Çerez Ayarları',
    pt: 'Definições de cookies',
    ja: 'クッキーの設定',
    zh: 'Cookie 设置',
    vi: 'Cài đặt cookie',
  },
  cookieSettingsRegion: {
    de: 'Cookie-Einstellungen',
    en: 'Cookie settings',
    es: 'Configuración de cookies',
    fr: 'Paramètres des cookies',
    it: 'Impostazioni dei cookie',
    nl: 'Cookie-instellingen',
    pl: 'Ustawienia plików cookie',
    tr: 'Çerez Ayarları',
    pt: 'Definições de cookies',
    ja: 'クッキーの設定',
    zh: 'Cookie 设置',
    vi: 'Cài đặt cookie',
  },
  cookieSettingsBody: {
    de: 'Damit unsere Website reibungslos funktioniert, speichern wir bei Bedarf nur wenige technische Informationen in Ihrem Browser. Marketing- und Analyse-Cookies setzen wir nicht. Mit „Akzeptieren“ stimmen Sie dem zu.',
    en: 'To keep this website running smoothly, we only store a few technical preferences in your browser when needed. We do not use marketing or analytics cookies. By choosing Accept, you agree to this.',
    es: 'Para que nuestra página web funcione correctamente, solo almacenamos, cuando es necesario, una pequeña cantidad de información técnica en su navegador. No utilizamos cookies de marketing ni de análisis. Al hacer clic en «Aceptar», da su consentimiento.',
    fr: "Afin d'assurer le bon fonctionnement de notre site web, nous enregistrons, si nécessaire, quelques informations techniques dans votre navigateur. Nous n'utilisons pas de cookies de marketing ni d'analyse. En cliquant sur « Accepter », vous donnez votre accord.",
    it: 'Per garantire il corretto funzionamento del nostro sito web, salviamo nel tuo browser solo alcune informazioni tecniche, se necessario. Non utilizziamo cookie di marketing né di analisi. Cliccando su “Accetta” dai il tuo consenso.',
    nl: 'Om ervoor te zorgen dat onze website soepel werkt, slaan we indien nodig slechts een klein aantal technische gegevens op in uw browser. We plaatsen geen marketing- en analysecookies. Door op ‘Accepteren’ te klikken, gaat u hiermee akkoord.',
    pl: 'Aby nasza strona internetowa działała bez zakłóceń, w razie potrzeby zapisujemy w Państwa przeglądarce jedynie niewielką ilość informacji technicznych. Nie stosujemy plików cookie służących do celów marketingowych ani analitycznych. Klikając „Akceptuję”, wyrażają Państwo na to zgodę.',
    tr: 'Web sitemizin sorunsuz çalışması için, gerektiğinde tarayıcınıza yalnızca az sayıda teknik bilgi kaydediyoruz. Pazarlama ve analiz amaçlı çerezler kullanmıyoruz. “Kabul Et” seçeneğine tıklayarak bunu kabul etmiş olursunuz.',
    pt: 'Para que o nosso site funcione sem problemas, armazenamos, quando necessário, apenas algumas informações técnicas no seu navegador. Não utilizamos cookies de marketing nem de análise. Ao clicar em «Aceitar», está a concordar com isto.',
    ja: '当ウェブサイトを円滑に機能させるため、必要に応じてお客様のブラウザに少量の技術情報を保存いたします。マーケティングや分析を目的としたクッキーは使用しておりません。「同意する」をクリックすることで、これに同意したものとみなされます。',
    zh: '为了确保本网站正常运行，我们仅会在必要时在您的浏览器中存储少量技术信息。我们不会设置营销和分析Cookie。点击“接受”即表示您同意此项。',
    vi: 'Để website chạy mượt, chúng tôi chỉ lưu một số tuỳ chọn kỹ thuật trên trình duyệt khi cần. Chúng tôi không dùng cookie tiếp thị hoặc phân tích. Chọn Chấp nhận nghĩa là bạn đồng ý với điều này.',
  },
  cookieSettingsPolicyLink: {
    de: 'Datenschutzerklärung',
    en: 'Privacy policy',
    es: 'Política de privacidad',
    fr: 'Politique de confidentialité',
    it: 'Informativa sulla privacy',
    nl: 'Privacyverklaring',
    pl: 'Polityka prywatności',
    tr: 'Gizlilik Beyanı',
    pt: 'Política de Privacidade',
    ja: 'プライバシーポリシー',
    zh: '隐私政策',
    vi: 'Chính sách bảo mật',
  },
  cookieSettingsAccept: {
    de: 'Akzeptieren',
    en: 'Accept',
    es: 'Aceptar',
    fr: 'Accepter',
    it: 'Accetta',
    nl: 'Accepteren',
    pl: 'Zaakceptuj',
    tr: 'Kabul et',
    pt: 'Aceitar',
    ja: '同意する',
    zh: '接受',
    vi: 'Chấp nhận',
  },
  cookieSettingsReject: {
    de: 'Ablehnen',
    en: 'Reject',
    es: 'Rechazar',
    fr: 'Refuser',
    it: 'Rifiuta',
    nl: 'Afwijzen',
    pl: 'Odrzuć',
    tr: 'Reddet',
    pt: 'Recusar',
    ja: '拒否する',
    zh: '拒绝',
    vi: 'Từ chối',
  },
  navMain: { de: 'Hauptnavigation', en: 'Main navigation', vi: 'Điều hướng chính' },
  navMenu: { de: 'Menü', en: 'Menu', vi: 'Menu' },
  navClose: { de: 'Menü schließen', en: 'Close menu', vi: 'Đóng menu' },
  navSubmenu: { de: 'Untermenü öffnen', en: 'Open submenu', vi: 'Mở menu con' },
  navLocale: { de: 'Sprache', en: 'Language', vi: 'Ngôn ngữ' },
  menuCategories: { de: 'Menükategorien', en: 'Menu categories', vi: 'Danh mục món' },
  /** Street Board tile meta — “3 Gerichte”. */
  menuItemCount: { de: 'Gerichte', en: 'dishes', vi: 'món' },
  menuLegalLegend: {
    de: 'Hinweise',
    en: 'Supplementary',
    vi: 'Ghi chú',
  },
  menuLegalAllergens: { de: 'Allergene', en: 'Allergens', vi: 'Chất gây dị ứng' },
  menuLegalAdditives: { de: 'Zusatzstoffe', en: 'Additives', vi: 'Phụ gia' },
  menuLegalDietary: { de: 'Ernährung', en: 'Dietary', vi: 'Chế độ ăn' },
  imprintTitle: {
    de: 'Impressum',
    en: 'Imprint',
    es: 'Aviso legal',
    fr: 'Mentions légales',
    it: 'Note legali',
    nl: 'Colofon',
    pl: 'Informacje prawne',
    tr: 'Yasal Uyarı',
    pt: 'Notas legais',
    ja: 'サイト運営者情報',
    zh: '网站信息',
    vi: 'Thông tin pháp lý',
  },
  privacyTitle: {
    de: 'Datenschutzerklärung',
    en: 'Privacy policy',
    es: 'Política de privacidad',
    fr: 'Politique de confidentialité',
    it: 'Informativa sulla privacy',
    nl: 'Privacyverklaring',
    pl: 'Polityka prywatności',
    tr: 'Gizlilik Beyanı',
    pt: 'Política de Privacidade',
    ja: 'プライバシーポリシー',
    zh: '隐私政策',
    vi: 'Chính sách bảo mật',
  },
  fieldName: { de: 'Name', en: 'Name', vi: 'Họ tên' },
  fieldEmail: { de: 'E-Mail', en: 'Email', vi: 'Email' },
  fieldPhone: { de: 'Telefon', en: 'Phone', vi: 'Điện thoại' },
  fieldGuests: { de: 'Personen', en: 'Guests', vi: 'Số khách' },
  fieldDate: { de: 'Datum', en: 'Date', vi: 'Ngày' },
  fieldTime: { de: 'Uhrzeit', en: 'Time', vi: 'Giờ' },
  fieldMessage: { de: 'Nachricht', en: 'Message', vi: 'Ghi chú' },
  fieldAllergens: {
    de: 'Allergene / Unverträglichkeiten',
    en: 'Allergies / intolerances',
    vi: 'Dị ứng / không dung nạp',
  },
  fieldTable: { de: 'Tisch', en: 'Table', vi: 'Bàn' },
  consentFallback: {
    de: 'Ich stimme der Verarbeitung meiner Angaben zu.',
    en: 'I agree to the processing of my details.',
    vi: 'Tôi đồng ý xử lý thông tin của mình.',
  },
  submitFallbackReservation: {
    de: 'Anfrage senden',
    en: 'Send request',
    vi: 'Gửi yêu cầu',
  },
  submitFallbackAllergy: {
    de: 'Sicher senden',
    en: 'Send securely',
    vi: 'Gửi an toàn',
  },
  formStatusIdle: {
    de: 'Pflichtfelder sind markiert.',
    en: 'Required fields are marked.',
    vi: 'Các trường bắt buộc được đánh dấu.',
  },
  formStatusSending: {
    de: 'Wird gesendet…',
    en: 'Sending…',
    vi: 'Đang gửi…',
  },
  formStatusSuccess: {
    de: 'Danke — wir melden uns in Kürze.',
    en: 'Thank you — we will get back to you shortly.',
    vi: 'Cảm ơn — chúng tôi sẽ sớm liên hệ.',
  },
  /** Reservation inquiry — request received, not an instant table confirmation. */
  reservationStatusSuccess: {
    de: 'Anfrage gesendet — wir bestätigen Ihren Tisch zeitnah per E-Mail oder Telefon.',
    en: 'Request sent — we will confirm your table shortly by email or phone.',
    vi: 'Đã gửi yêu cầu — chúng tôi sẽ xác nhận bàn sớm qua email hoặc điện thoại.',
  },
  formStatusError: {
    de: 'Senden fehlgeschlagen. Bitte erneut versuchen.',
    en: 'Could not send. Please try again.',
    vi: 'Gửi thất bại. Vui lòng thử lại.',
  },
  formStatusPreview: {
    de: 'Vorschau: Formular wäre jetzt gesendet.',
    en: 'Preview: the form would be submitted now.',
    vi: 'Xem trước: biểu mẫu sẽ được gửi.',
  },
  reservationNextThirdParty: {
    de: 'Sie buchen beim Reservierungspartner. Die Bestätigung kommt von dort (E-Mail/SMS).',
    en: 'You book with our reservation partner. Confirmation comes from them (email/SMS).',
    vi: 'Bạn đặt chỗ qua đối tác. Xác nhận sẽ đến từ họ (email/SMS).',
  },
  /** Direct-contact CTAs are self-explanatory — no extra “next step” line. */
  reservationNextMessenger: {
    de: '',
    en: '',
    vi: '',
  },
  reservationNextCall: {
    de: '',
    en: '',
    vi: '',
  },
  reservationNextDirect: {
    de: '',
    en: '',
    vi: '',
  },
  reservationCtaMessenger: {
    de: 'WhatsApp',
    en: 'WhatsApp',
    vi: 'WhatsApp',
  },
  reservationCtaCall: {
    de: 'Anrufen',
    en: 'Call',
    vi: 'Gọi',
  },
  requiredMark: { de: 'Pflichtfeld', en: 'Required', vi: 'Bắt buộc' },
  galleryClose: { de: 'Schließen', en: 'Close', vi: 'Đóng' },
  galleryPrev: { de: 'Vorheriges Bild', en: 'Previous photo', vi: 'Ảnh trước' },
  galleryNext: { de: 'Nächstes Bild', en: 'Next photo', vi: 'Ảnh sau' },
  galleryFilmstripNav: {
    de: 'Fotostreifen',
    en: 'Photo strip',
    vi: 'Dải ảnh',
  },
  galleryGoToPhoto: {
    de: 'Zu Foto {n}',
    en: 'Go to photo {n}',
    vi: 'Đến ảnh {n}',
  },
  highlightPromenadeNav: {
    de: 'Gericht-Promenade',
    en: 'Dish promenade',
    vi: 'Dải món nổi bật',
  },
  highlightPromenadeGoTo: {
    de: 'Zu Gericht {n}',
    en: 'Go to dish {n}',
    vi: 'Đến món {n}',
  },
  highlightZineNav: {
    de: 'Kartenstapel',
    en: 'Card stack',
    vi: 'Chồng thẻ món',
  },
  highlightZineGoTo: {
    de: 'Zu Karte {n}',
    en: 'Go to card {n}',
    vi: 'Đến thẻ {n}',
  },
  highlightHeroPlateNav: {
    de: 'Empfohlene Gerichte',
    en: 'Featured dishes',
    vi: 'Món nổi bật',
  },
  highlightHeroPlateGoTo: {
    de: 'Zu Gericht {n}',
    en: 'Go to dish {n}',
    vi: 'Đến món {n}',
  },
  backToTopLabel: {
    de: 'Nach oben',
    en: 'Back to top',
    vi: 'Lên đầu trang',
  },
  backToTopAria: {
    de: 'Zum Seitenanfang',
    en: 'Back to top of page',
    vi: 'Về đầu trang',
  },
  contactChannelsRegion: {
    de: 'Kontaktkanäle',
    en: 'Contact channels',
    vi: 'Kênh liên hệ',
  },
  contactChannelsExpand: {
    de: 'Kontakt öffnen',
    en: 'Open contact',
    vi: 'Mở liên hệ',
  },
  contactChannelsCollapse: {
    de: 'Kontakt schließen',
    en: 'Close contact',
    vi: 'Đóng liên hệ',
  },
  contactChannelsPhone: {
    de: 'Anrufen',
    en: 'Call',
    vi: 'Gọi',
  },
  utilityBarRegion: {
    de: 'Kontaktleiste',
    en: 'Utility bar',
    vi: 'Thanh tiện ích',
  },
  utilityBarDismiss: {
    de: 'Leiste schließen',
    en: 'Dismiss bar',
    vi: 'Đóng thanh',
  },
  utilityBarExpand: {
    de: 'Kontaktleiste öffnen',
    en: 'Expand utility bar',
    vi: 'Mở thanh tiện ích',
  },
  utilityBarCollapse: {
    de: 'Kontaktleiste schließen',
    en: 'Collapse utility bar',
    vi: 'Thu gọn thanh tiện ích',
  },
  utilityBarEmpty: {
    de: 'Einträge hinzufügen',
    en: 'Add items',
    vi: 'Thêm mục',
  },
} as const satisfies Record<string, LocalizedString>;

export type SiteChromeKey = keyof typeof SITE_CHROME;

export function pickChrome(key: SiteChromeKey, locale: string): string {
  return pickLocalized(SITE_CHROME[key], locale);
}

export function pickImprintFieldLabel(
  fieldKey: (typeof LEGAL_IMPRINT_FIELD_KEYS)[number],
  locale: string,
  override?: string | null,
): string {
  const trimmed = override?.trim();
  if (trimmed) {
    return trimmed;
  }
  return pickLocalized(LEGAL_IMPRINT_DEFAULT_LABELS[fieldKey], locale);
}

export function pickPrivacyFieldLabel(
  fieldKey: (typeof LEGAL_PRIVACY_FIELD_KEYS)[number],
  locale: string,
  override?: string | null,
): string {
  const trimmed = override?.trim();
  if (trimmed) {
    return trimmed;
  }
  return pickLocalized(LEGAL_PRIVACY_DEFAULT_LABELS[fieldKey], locale);
}

export { legalFieldLabelContentKey };
